import type { Masthead, ResolvedMasthead } from '@shared/types/Masthead.types';
import { MASTHEAD_ASSET_MOCKS } from './mastheadAssetMocks';
import { URLS } from '@shared/utils/urls';

// Interim catalog entries until the final design set lands; swatchColor values are
// placeholders until the final palette. The BE stores only mastheadId (uuid); slug names
// the S3 asset, artworkId groups colorways for the admin picker.
// S3 naming: mastheads/{slug}[@2x|@3x].png
const MASTHEADS: ReadonlyArray<Masthead> = [
  {
    mastheadId: '55c02b89-40af-4758-94be-022064f27a69',
    slug: 'top_botanical_emerald',
    artworkId: 'top_botanical',
    placement: 'top',
    swatchColor: '#008000',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: '0b2ca5d3-5e76-4d1f-b8ff-877869c567f9',
    slug: 'top_botanical_red',
    artworkId: 'top_botanical',
    placement: 'top',
    swatchColor: '#FF0000',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: 'f32c4948-b540-4b1d-ab56-929705fbfc6c',
    slug: 'top_crest_gold',
    artworkId: 'top_crest',
    placement: 'top',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: 'c3d2bf31-b03a-4db1-9fab-45171526c6d1',
    slug: 'frame_laurel_gold',
    artworkId: 'frame_laurel',
    placement: 'center',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: '2b6df7cf-91a2-4673-91de-f8555cad9ea7',
    slug: 'frame_laurel_silver',
    artworkId: 'frame_laurel',
    placement: 'center',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: 'd65af1bc-8bab-4382-b061-fd6126dfda6d',
    slug: 'frame_oval_gold',
    artworkId: 'frame_oval',
    placement: 'center',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: 'b57cd97b-9f50-47ec-ba54-f5c31c2b61d2',
    slug: 'princess_pine',
    artworkId: 'princess',
    placement: 'top',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: '653def9a-d7b3-4137-9d9e-df564e4f8633',
    slug: 'princess_tuxedo',
    artworkId: 'princess',
    placement: 'top',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: '92835d7b-df0e-47fc-b684-04bbb3cb5c2d',
    slug: 'princess_cream',
    artworkId: 'princess',
    placement: 'top',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: 'ca96962d-2613-41d1-8aae-ac0a4cf373f5',
    slug: 'campania_yellow',
    artworkId: 'campania',
    placement: 'center',
    swatchColor: '#9d8562',
    width: 1600,
    height: 600,
  },
  {
    mastheadId: '1d89b319-21c7-4ff2-af9c-6189ebcdbe5d',
    slug: 'art_disco_ball_orange',
    artworkId: 'art_disco_ball',
    placement: 'top',
    swatchColor: '#ed784a',
    width: 986,
    height: 370,
  },
  {
    mastheadId: '49e0bb77-a3c4-4193-9c2a-d65d30b0a66d',
    slug: 'top_art_chandelier_red',
    artworkId: 'top_art_chandelier',
    placement: 'top',
    swatchColor: '#b6292c',
    width: 986,
    height: 370,
  },
  {
    mastheadId: '4d7349a6-b0d0-4f97-a952-a8d49f49cbd2',
    slug: 'top_art_ornaments_blue',
    artworkId: 'ornaments',
    placement: 'top',
    swatchColor: '#2840a0',
    width: 613,
    height: 230,
  },
];

// Indexed by mastheadId (the canonical BE-stored uuid) and by slug, so dev query params
// can use the readable slug.
const mastheadsMap = new Map(MASTHEADS.flatMap(masthead => [[masthead.mastheadId, masthead] as const, [masthead.slug, masthead] as const]));

/**
 * Resolve a stored mastheadId (or dev-convenience slug) into a renderable accent. Returns
 * null when the accent is off or the id doesn't validate against the catalog (render nothing).
 */
export const resolveMasthead = (mastheadId: Maybe<string>): ResolvedMasthead | null => {
  const masthead = mastheadId ? mastheadsMap.get(mastheadId) : undefined;
  if (!masthead) {
    return null;
  }
  return {
    masthead,
    assetUrl: MASTHEAD_ASSET_MOCKS[masthead.slug] ?? URLS.masthead(masthead.slug),
  };
};
